<script setup lang="ts">
import { computed } from "vue"
import { useRouter } from "vue-router"
import { Hash, KanbanSquare, UserRound, Users, Volume2 } from "lucide-vue-next"
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command"
import { useGroupsStore } from "~/stores/groups"
import { useFriendsStore } from "~/stores/friends"
import { useQuickSwitcher } from "~/composables/useQuickSwitcher"
import { ChannelType } from "~/types/groups"

// Quick switcher: type a few letters, jump to a group, a channel or a friend (⌘K), or just to a
// channel of the current group (⇧⌘O) — see lib/settings/hotkeys.ts and useGlobalHotkeys.ts.

const { isOpen, mode } = useQuickSwitcher()
const groupsStore = useGroupsStore()
const friendsStore = useFriendsStore()
const router = useRouter()

interface ChannelEntry { id: string, name: string, voice: boolean, board: boolean, groupId: string, groupName: string }

const channels = computed<ChannelEntry[]>(() => {
  const groupIds = mode.value === "channels"
    ? (groupsStore.selectedGroupId ? [groupsStore.selectedGroupId] : [])
    : Object.keys(groupsStore.groupDetails)
  const result: ChannelEntry[] = []
  for (const groupId of groupIds) {
    const details = groupsStore.groupDetails[groupId]
    if (!details) continue
    for (const category of details.categories ?? []) {
      for (const channel of category.channels) {
        if (channel.type !== ChannelType.Text && channel.type !== ChannelType.Voice && channel.type !== ChannelType.Board) continue
        result.push({
          id: channel.id,
          name: channel.name,
          voice: channel.type === ChannelType.Voice,
          board: channel.type === ChannelType.Board,
          groupId,
          groupName: details.name,
        })
      }
    }
  }
  return result
})

const groups = computed(() => (mode.value === "all" ? groupsStore.groups : []))
const friends = computed(() => (mode.value === "all" ? friendsStore.friends : []))

const placeholder = computed(() => (mode.value === "channels" ? "Канал этой группы…" : "Группа, канал или друг…"))

function go(to: Parameters<typeof router.push>[0]) {
  isOpen.value = false
  void router.push(to)
}
</script>

<template>
  <CommandDialog v-model:open="isOpen" title="Быстрый переход" description="Группы, каналы и друзья">
    <CommandInput :placeholder="placeholder" data-testid="quick-switcher-input"/>
    <CommandList data-testid="quick-switcher">
      <CommandEmpty>Ничего не нашлось</CommandEmpty>
      <CommandGroup v-if="channels.length" heading="Каналы">
        <CommandItem
            v-for="channel in channels"
            :key="channel.id"
            :value="`${channel.name} ${channel.groupName} ${channel.id}`"
            @select="go({ path: `/group/${channel.groupId}`, query: { channel: channel.id } })"
        >
          <Volume2 v-if="channel.voice" class="text-muted-foreground"/>
          <KanbanSquare v-else-if="channel.board" class="text-muted-foreground"/>
          <Hash v-else class="text-muted-foreground"/>
          <span class="truncate">{{ channel.name }}</span>
          <span v-if="mode === 'all'" class="ml-auto truncate text-xs text-muted-foreground">{{ channel.groupName }}</span>
        </CommandItem>
      </CommandGroup>
      <CommandGroup v-if="groups.length" heading="Группы">
        <CommandItem
            v-for="group in groups"
            :key="group.id"
            :value="`${group.name} ${group.id}`"
            @select="go(`/group/${group.id}`)"
        >
          <Users class="text-muted-foreground"/>
          <span class="truncate">{{ group.name }}</span>
        </CommandItem>
      </CommandGroup>
      <CommandGroup v-if="friends.length" heading="Друзья">
        <CommandItem
            v-for="friend in friends"
            :key="friend.other_user_id"
            :value="`${friend.other_display_name} ${friend.other_username} ${friend.other_user_id}`"
            @select="go({ path: '/dashboard', query: { friend: friend.other_user_id } })"
        >
          <UserRound class="text-muted-foreground"/>
          <span class="truncate">{{ friend.other_display_name || friend.other_username }}</span>
          <span class="ml-auto text-xs text-muted-foreground">@{{ friend.other_username }}</span>
        </CommandItem>
      </CommandGroup>
    </CommandList>
  </CommandDialog>
</template>
